import { INVITE_LINK_ERRORS, INVITE_LINK_START_PARAM_PREFIX } from '@/constants';
import type { InviteLinkError } from '@/constants';
import type { InviteLinkPreview } from '@/types/inviteLink';

// Tokens are base64url; anything else can't be a valid link (validated again server-side).
const TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/;

const INVITE_LINK_ERROR_CODES = new Set<string>(Object.values(INVITE_LINK_ERRORS));

export function isInviteLinkTokenFormat(token: string | undefined): token is string {
  return !!token && TOKEN_PATTERN.test(token);
}

export function buildJoinPath(token: string): string {
  return `/join/${encodeURIComponent(token)}`;
}

/** Public web URL encoded into the link / QR code. */
export function buildInviteLinkUrl(token: string): string {
  return `${window.location.origin}${buildJoinPath(token)}`;
}

/**
 * Telegram Mini App deep link (`https://t.me/<bot>/<app>?startapp=inv_<token>`).
 * Returns null when `VITE_TELEGRAM_MINI_APP_URL` is not configured — the feature is optional.
 */
export function buildTelegramStartAppUrl(token: string): string | null {
  const base: unknown = import.meta.env.VITE_TELEGRAM_MINI_APP_URL;
  if (typeof base !== 'string' || base.trim().length === 0) return null;
  return `${base.trim()}?startapp=${INVITE_LINK_START_PARAM_PREFIX}${token}`;
}

/** Telegram share sheet URL for a link. */
export function buildTelegramShareUrl(url: string, text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
}

/** Extracts the invite token from a Mini App `start_param` (`inv_<token>`), or null. */
export function parseInviteStartParam(startParam: string | null | undefined): string | null {
  if (!startParam || !startParam.startsWith(INVITE_LINK_START_PARAM_PREFIX)) return null;
  const token = startParam.slice(INVITE_LINK_START_PARAM_PREFIX.length);
  return isInviteLinkTokenFormat(token) ? token : null;
}

/**
 * Inviter label on the join page: "First Last" when both names are set,
 * otherwise "@username"; a lone first name is the last resort.
 */
export function getInviterDisplayName(inviter: InviteLinkPreview['inviter']): string | null {
  if (!inviter) return null;
  const firstName = inviter.firstName?.trim();
  const lastName = inviter.lastName?.trim();
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (inviter.username) return `@${inviter.username}`;
  return firstName || null;
}

/** Maps an API error to an `INVITE_LINK_ERRORS` code when the backend returned one. */
export function getInviteLinkErrorCode(error: unknown): InviteLinkError | null {
  const code = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof code === 'string' && INVITE_LINK_ERROR_CODES.has(code) ? (code as InviteLinkError) : null;
}

/** Server-provided message of an API error, if any. */
export function getApiErrorMessage(error: unknown): string | null {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.length > 0 ? message : null;
}
